import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { Hub } from 'aws-amplify/utils';

export interface AuthUser {
  cognitoSub: string;
  username: string;
  email: string;
  UserStateID: number | null;
  playerFirstName: string;
  playerLastName: string;
  profileImageUrl?: string | null;
  provider?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<any>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<any>;
  resendCode: (email: string) => Promise<any>;
  resetPassword: (email: string) => Promise<any>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<any>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if login should be skipped
  const skipLogin = import.meta.env.VITE_SKIP_LOGIN === 'True';

  // Check if user is authenticated on mount
  useEffect(() => {
    checkUser();
  }, []);

  // Listen to auth events
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('User signed in');
          checkUser();
          break;
        case 'signedOut':
          console.log('User signed out');
          setUser(null);
          setIsAuthenticated(false);
          break;
        case 'tokenRefresh':
          console.log('Token refreshed');
          checkUser();
          break;
        case 'tokenRefresh_failure':
          console.log('Token refresh failed');
          setUser(null);
          setIsAuthenticated(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  const checkUser = async () => {
    try {
      // Skip login - use test user
      if (skipLogin) {
        console.log('🧪 VITE_SKIP_LOGIN enabled - using test user');
        const testUser: AuthUser = {
          cognitoSub: 'test-user-id',
          username: 'testuser',
          email: 'test@example.com',
          UserStateID: 1,
          playerFirstName: 'Test',
          playerLastName: 'User',
          profileImageUrl: null,
          provider: 'test'
        };
        setUser(testUser);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // Normal authentication flow
      const result = await authService.getCurrentUser();
      if (result.success && result.user) {
        setUser(result.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const result = await authService.signIn({ email, password });
    if (result.success) {
      await checkUser();
    }
    return result;
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    return await authService.signUp({ email, password, firstName, lastName });
  };

  const signOut = async () => {
    await authService.signOut();
    setUser(null);
    setIsAuthenticated(false);
  };

  const confirmSignUp = async (email: string, code: string) => {
    return await authService.confirmSignUp({ email, code });
  };

  const resendCode = async (email: string) => {
    return await authService.resendVerificationCode(email);
  };

  const resetPassword = async (email: string) => {
    return await authService.resetPassword({ email });
  };

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    return await authService.confirmResetPassword({ email, code, newPassword });
  };

  const refreshUser = async () => {
    await checkUser();
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    resendCode,
    resetPassword,
    confirmResetPassword,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
